import {
  useActiveVkuiLocation,
  useParams,
  useRouteNavigator,
} from '@vkontakte/vk-mini-apps-router';
import { FormItem, Textarea, type NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { ApiError, api, errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { useSession } from '../../api/session';
import {
  DORM_LABELS,
  FORM_FIELDS,
  FORM_FIELD_LABELS,
  CHARACTER_KIND_LABELS,
  REVIEW_STATUS_LABELS,
  STUDY_CLASS_LABELS,
  type Author,
  type CharacterKind,
  type Dorm,
  type FormAbilityEntry,
  type FormField,
  type FormFields,
  type ModerationVerdict,
  type ReviewStatus,
  type StudyClass,
} from '../../api/types';
import { countersChanged } from '../../app/counters';
import { leaveGuarded } from '../../app/leaveGuard';
import { VIEW, VIEW_ROOT_PATH } from '../../app/router';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { versionLabel } from '../../shared/lib/docNumber';
import type { ApiImage } from '../../shared/lib/media';
import {
  Action,
  ActionRow,
  CardHeading,
  CardList,
  ChipRow,
  ErrorState,
  ExternalLink,
  FieldLabel,
  Gap,
  Gutter,
  LoadingState,
  Media,
  Meta,
  Muted,
  NoAccess,
  Screen,
  Section,
  SectionTitle,
  Select,
  StatusBadge,
  Thumb,
  toast,
  type StatusTone,
} from '../../shared/ui';
// Общий компонент «было / стало». Картинку он тоже рисует сам — kind='image',
// значение при этом идентификатор картинки строкой, как и отдаёт бэкенд.
import { FieldDiff, type FieldDiffKind } from '../characters/FieldDiff';
import {
  DORM_OPTIONS,
  STUDY_CLASS_OPTIONS,
  Notice,
  NoticeBlock,
  STALE_MESSAGE,
  Tag,
  canAct,
  canReviewForms,
  forgetDecided,
  formatDateTime,
  isConflict,
  personName,
  vkProfileUrl,
} from './staffKit';

/**
 * Разбор заявки на анкету — главный экран модерации.
 *
 * Три правила проекта, которые здесь не украшение, а суть экрана:
 *  1. принять анкету можно ТОЛЬКО с назначенным общежитием;
 *  2. отказ — только с непустым комментарием;
 *  3. решение уходит вместе с revision, который пришёл с сервера: игрок
 *     правит заявку прямо во время проверки, и модератор мог смотреть на
 *     устаревший текст. Разошлось — сервер отвечает конфликтом, мы честно
 *     говорим «заявка изменилась» и перечитываем данные.
 */

interface ModeratedVersion extends FormFields {
  id: number;
  versionNo: number;
  status: ReviewStatus;
  revision: number;
  baseVersionId: number | null;
  submittedAt: string | null;
  diffFieldCodes: string[] | null;
  /** Адреса вида /api/media/:id. Ключей хранилища здесь нет и быть не может. */
  appearanceImage: ApiImage | null;
}

interface FieldChange {
  field: FormField;
  kind: 'text' | 'image' | 'abilities';
  label: string;
  before: string | null;
  after: string | null;
}

interface FormDetail {
  version: ModeratedVersion;
  published: ModeratedVersion | null;
  diff: FieldChange[];
  isInitial: boolean;
  /** Именно это значение уходит обратно вместе с решением. */
  revision: number;
  character: {
    id: number;
    kind: CharacterKind;
    dorm: Dorm | null;
    studyClass: StudyClass | null;
    publishedVersionId: number | null;
    publishedAt: string | null;
  };
  author: Author;
}

type Outcome = { verdict: ModerationVerdict; text: string } | null;

const STATUS_TONE: Record<ReviewStatus, StatusTone> = {
  draft: 'archived',
  pending: 'pending',
  approved: 'published',
  rejected: 'rejected',
};

export function FormReviewPanel({ id }: NavIdProps) {
  const session = useSession();
  const back = useBack();
  const routeNavigator = useRouteNavigator();
  const { panel } = useActiveVkuiLocation();
  const params = useParams();

  const active = id !== undefined && panel === id;
  const raw = params?.versionId;
  const versionId = raw && /^\d+$/.test(raw) ? Number(raw) : null;

  const detail = useLoader<FormDetail>(
    (signal) => api.get<FormDetail>(`/api/characters/versions/${versionId}`, undefined, signal),
    [versionId],
    active && versionId !== null && canReviewForms(session),
  );

  const [dorm, setDorm] = useState<Dorm | ''>('');
  const [studyClass, setStudyClass] = useState<StudyClass | ''>('');
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [comment, setComment] = useState('');
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [showSame, setShowSame] = useState(false);

  // Два админских поля решения, и оба обязательны: без общежития и без класса
  // анкету не принять. Класс добавлен 20.09.2026 — из него считается выпускник,
  // и «поставлю потом в админке» означало персонажа в реестре без разряда.
  // Раньше поле отсюда убирали как отвлекающее — решение отменено владельцем.
  // Подставляем уже назначенное: при правке анкеты менять его обычно не надо.
  const data = detail.data;
  // Засеваем один раз на версию: перечитывание после 409 не должно стирать
  // уже выбранное проверяющим.
  const [seededVersion, setSeededVersion] = useState<number | null>(null);
  if (data && data.version.id !== seededVersion) {
    setSeededVersion(data.version.id);
    setDorm(data.character.dorm ?? '');
    setStudyClass(data.character.studyClass ?? '');
  }

  if (!canReviewForms(session)) {
    return (
      <Screen id={id} title={t('common.section.form')} back={back}>
        <NoAccess>{t('review.form.noAccess')}</NoAccess>
      </Screen>
    );
  }

  async function decide(action: 'approve' | 'reject') {
    if (!data) return;
    // Страховка на случай, если кнопки когда-нибудь перестанут гасить: без
    // общежития анкету не принять, без комментария — не отклонить.
    const reason = comment.trim();
    if (action === 'approve' && (dorm === '' || studyClass === '')) return;
    if (action === 'reject' && reason === '') return;
    setBusy(true);
    try {
      const body =
        action === 'approve'
          ? { revision: data.revision, dorm, studyClass }
          : { revision: data.revision, comment: reason };

      await api.post(`/api/characters/versions/${data.version.id}/${action}`, body);

      forgetDecided('form', data.version.id);
      countersChanged();
      setStale(false);
      // Результат решения — тост, а не блок на экране: это ответ на действие,
      // а не состояние заявки. Кнопка в нём ведёт в очередь адресом, а не
      // `back()`: тост переживает экран, и вернувшегося модератора шаг назад
      // увёл бы из очереди.
      const result: NonNullable<Outcome> =
        action === 'approve'
          ? {
              verdict: 'approve',
              text: t('review.form.approved', {
                dorm: dorm ? DORM_LABELS[dorm] : '—',
                studyClass: studyClass ? STUDY_CLASS_LABELS[studyClass] : '—',
              }),
            }
          : { verdict: 'reject', text: t('review.form.rejected') };
      setOutcome(result);
      toast(result.text, {
        label: t('common.action.backToQueue'),
        onClick: () => leaveGuarded(() => routeNavigator.replace(VIEW_ROOT_PATH[VIEW.TASKS])),
      });
    } catch (cause: unknown) {
      // Молча повторять запрос нельзя: заявку могли переписать целиком, и
      // решение относилось бы уже к другому тексту.
      if (isConflict(cause)) {
        setStale(true);
        detail.reload();
      } else if (cause instanceof ApiError && cause.status === 404) {
        toast(t('review.form.notFound'));
      } else {
        toast(errorMessage(cause));
      }
    } finally {
      setBusy(false);
    }
  }

  // У первичной анкеты в diff попадает вообще всё: сравнивать не с чем, и
  // подсветка «изменено» на каждом поле была бы шумом, а не подсказкой.
  const changes =
    data && !data.isInitial
      ? new Map<FormField, FieldChange>(data.diff.map((change) => [change.field, change]))
      : null;
  const changedCount = changes?.size ?? 0;

  // Порядок анкеты сохраняем в обеих половинах, но изменённое идёт первым:
  // ради него экран и открывают, а четырнадцать одинаковых блоков подряд
  // заставляли искать правку глазами.
  const changedFields = FORM_FIELDS.filter((field) => changes?.has(field) === true);
  const sameFields = FORM_FIELDS.filter((field) => changes?.has(field) !== true);
  const sameOpen = changedFields.length === 0 || showSame;

  const decided = data !== null && data.version.status !== 'pending';
  const writable = canAct(session);
  const dormMissing = dorm === '';
  const classMissing = studyClass === '';
  // ФИО на русском требует сервер при приёме: без этой проверки кнопка обещала
  // бы то, чего сервер не сделает, и модератор получал бы 400 вместо подсказки.
  const nameMissing = (data?.version.nameRu ?? '').trim() === '';
  // Отказ прошлого раза поднят наверх экрана: проверяющий должен прочитать
  // требование до того, как начнёт читать текст, иначе разберёт то же заново.
  const name = data
    ? data.version.nameRu?.trim() || data.version.nameEn?.trim() || t('common.empty.noName')
    : '';

  /*
    Состояние заявки — в шапке: разбор открывают, уже зная, ЧТО
    проверяют, и первым делом смотрят, решена ли она. У имени персонажа
    значок спорил с самим именем.
  */
  return (
    <Screen
      id={id}
      title={t('common.section.form')}
      back={back}
      after={
        data ? (
          <StatusBadge tone={STATUS_TONE[data.version.status]}>
            {REVIEW_STATUS_LABELS[data.version.status].toLocaleLowerCase('ru')}
          </StatusBadge>
        ) : undefined
      }
    >
      {detail.loading && !data ? <LoadingState text={t('review.form.loading')} /> : null}
      {detail.error ? <ErrorState message={detail.error} onRetry={detail.reload} /> : null}

      {data ? (
        <>
          {/* ── Сводка ───────────────────────────────────────────────── */}
          <Section>
            <Gutter>
              <Media
                aside={
                  <Thumb
                    image={data.version.appearanceImage}
                    alt={t('review.form.appearanceAlt', { name })}
                    gallery={[data.version.appearanceImage]}
                  />
                }
              >
                <CardHeading>{name}</CardHeading>

                {data.version.nameEn?.trim() ? (
                  <>
                    <Gap step="s1" />
                    <Muted>{data.version.nameEn.trim()}</Muted>
                  </>
                ) : null}

                <Gap step="s2" />
                <Meta>
                  {[
                    versionLabel(data.version.versionNo),
                    formatDateTime(data.version.submittedAt),
                    <ExternalLink key="author" href={vkProfileUrl(data.author.vkUserId)}>
                      {personName(data.author)}
                    </ExternalLink>,
                    // Правки после отправки значат, что текст на экране мог
                    // разойтись с тем, который проверяющий открыл минуту назад.
                    data.revision === 0
                      ? null
                      : t('review.form.revision', { count: data.revision }),
                  ]}
                </Meta>
              </Media>

              <Gap step="s3" />
              <ChipRow>
                <Tag>{CHARACTER_KIND_LABELS[data.character.kind]}</Tag>
                {data.character.dorm ? <Tag>{DORM_LABELS[data.character.dorm]}</Tag> : null}
                {data.character.studyClass ? (
                  <Tag>
                    {t('review.form.studyClass.tag', {
                      value: STUDY_CLASS_LABELS[data.character.studyClass],
                    })}
                  </Tag>
                ) : null}
              </ChipRow>

              {data.isInitial ? (
                <>
                  <Gap step="s3" />
                  <Muted>{t('review.form.initial')}</Muted>
                </>
              ) : null}
            </Gutter>
          </Section>

          {/* ── Конфликт и результат ─────────────────────────────────── */}
          {stale ? (
            <NoticeBlock
              tone="warning"
              title={STALE_MESSAGE}
              action={
                <Action tone="quiet" size="s" onClick={detail.reload}>
                  {t('common.action.refresh')}
                </Action>
              }
            >
              {t('review.form.stale')}
            </NoticeBlock>
          ) : null}

          {!outcome && decided ? (
            <NoticeBlock tone="info" title={t('review.form.decided.title')}>
              {t('review.form.decided.text', {
                status: REVIEW_STATUS_LABELS[data.version.status],
              })}
            </NoticeBlock>
          ) : null}

          {/* ── Поля анкеты ──────────────────────────────────────────── */}
          {changedFields.length > 0 ? (
            <Section>
              <SectionTitle accent="pink">
                {t('review.claim.changed', { count: changedCount })}
              </SectionTitle>
              <FieldList version={data.version} changes={changes} fields={changedFields} />
            </Section>
          ) : null}

          {sameFields.length > 0 ? (
            <Section>
              <SectionTitle>
                {changedFields.length > 0 ? t('review.claim.same') : t('review.form.fields')}
              </SectionTitle>

              {/* Раскрывашка ПОД списком, а не над ним: до нажатия иначе не
                  видно ни одной строки, и раздел выглядит пустым. Открытые
                  поля кончаются там же, куда человек только что нажал. */}
              {sameOpen ? (
                <FieldList version={data.version} changes={changes} fields={sameFields} compact />
              ) : null}

              {changedFields.length > 0 ? (
                <Gutter>
                  <Action
                    tone="more"
                    size="m"
                    stretched
                    aria-expanded={showSame}
                    onClick={() => setShowSame(!showSame)}
                  >
                    {showSame
                      ? t('common.action.collapse')
                      : t('review.claim.showMore', { count: sameFields.length })}
                  </Action>
                </Gutter>
              ) : null}
            </Section>
          ) : null}

          {/* ── Решение ──────────────────────────────────────────────── */}
          {!decided && !outcome ? (
            <Section>
              <SectionTitle accent="pink">{t('review.decision.title')}</SectionTitle>
              <FormItem top={<FieldLabel>{t('review.form.dorm.label')}</FieldLabel>}>
                <Select
                  value={dorm}
                  disabled={busy || !writable}
                  placeholder={t('review.form.dorm.placeholder')}
                  options={DORM_OPTIONS}
                  label={t('review.form.dorm.label')}
                  onChange={(event) => setDorm(event.target.value as Dorm | '')}
                />
              </FormItem>

              <FormItem top={<FieldLabel>{t('review.form.studyClass.label')}</FieldLabel>}>
                <Select
                  value={studyClass}
                  disabled={busy || !writable}
                  placeholder={t('review.form.studyClass.placeholder')}
                  options={STUDY_CLASS_OPTIONS}
                  label={t('review.form.studyClass.label')}
                  onChange={(event) => setStudyClass(event.target.value as StudyClass | '')}
                />
              </FormItem>

              <FormItem top={<FieldLabel>{t('review.decision.comment.label')}</FieldLabel>}>
                <Textarea
                  value={comment}
                  rows={3}
                  disabled={busy || !writable}
                  placeholder={t('review.decision.comment.placeholder')}
                  onChange={(event) => setComment(event.target.value)}
                />
              </FormItem>

              {!writable ? (
                <Gutter>
                  <Notice tone="warning" title={t('review.decision.readOnly.title')}>
                    {t('review.form.readOnly')}
                  </Notice>
                </Gutter>
              ) : null}

              <Gutter>
                <ActionRow>
                  <Action
                    tone="action"
                    size="l"
                    stretched
                    loading={busy}
                    disabled={busy || dormMissing || classMissing || nameMissing || !writable}
                    onClick={() => void decide('approve')}
                  >
                    {t('review.decision.accept')}
                  </Action>
                  <Action
                    tone="quiet"
                    size="l"
                    stretched
                    disabled={busy || !writable || comment.trim() === ''}
                    onClick={() => void decide('reject')}
                  >
                    {t('review.decision.reject')}
                  </Action>
                </ActionRow>
              </Gutter>
            </Section>
          ) : null}
        </>
      ) : null}
    </Screen>
  );
}

/* ── Значения полей ──────────────────────────────────────────────────────── */

/** Половина анкеты подряд: изменённая или нетронутая — решает вызывающий. */
function FieldList({
  version,
  changes,
  fields,
  compact = false,
}: {
  version: ModeratedVersion;
  changes: Map<FormField, FieldChange> | null;
  fields: readonly FormField[];
  /** Нетронутые поля — строками «подпись — значение», как в макете. */
  compact?: boolean;
}) {
  return (
    <CardList>
      {fields.map((field) => {
        const change = changes?.get(field) ?? null;
        // У изменённого поля оба значения берём из diff'а сервера: там они
        // нормализованы одинаково, и «было» со «стало» не разъедутся на лишнем
        // пробеле. Для неизменённого diff'а нет — читаем версию.
        return (
          <FieldDiff
            key={field}
            label={FORM_FIELD_LABELS[field]}
            kind={fieldKind(field)}
            before={change?.before ?? null}
            after={change ? change.after : fieldText(version, field)}
            changed={change !== null}
            compact={compact}
            beforeLabel={t('review.form.before')}
            afterLabel={t('review.claim.after')}
          />
        );
      })}
    </CardList>
  );
}

/** Чем поле является для отрисовки. Совпадает с kind из diff'а бэкенда. */
function fieldKind(field: FormField): FieldDiffKind {
  if (field === 'appearanceImageId') return 'image';
  if (field === 'abilities') return 'abilities';
  return 'text';
}

/** Текст поля для показа. Пустое поле и отсутствующее — одно и то же. */
function fieldText(version: ModeratedVersion, field: FormField): string | null {
  if (field === 'abilities') return abilitiesToText(version.abilities);
  if (field === 'appearanceImageId') {
    return version.appearanceImageId === null ? null : String(version.appearanceImageId);
  }
  const value: unknown = version[field];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function abilitiesToText(entries: FormAbilityEntry[]): string | null {
  const lines: string[] = [];

  for (const entry of entries) {
    const title = entry.title.trim();
    const description = (entry.description ?? '').trim();
    if (!title && !description) continue;
    lines.push(description ? `${title}: ${description}` : title);
  }

  return lines.length > 0 ? lines.join('\n') : null;
}
